/**
 * Fondos Page — Card-based UI
 * src/pages/Fondos.jsx
 *
 * Card: person color bar, tipo, monto, tasa%, proyectado (green), ganancia (blue).
 * Modal: Person, Tipo, Fecha Inversion, Monto, Tasa Interes, Meses. Proyectado/Ganancia read-only.
 * A = P(1 + r/12)^n  where r = annual rate as decimal, n = months
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { showToast } from '../components/Toast';
import { useSettings } from '../context/SettingsContext';
import { logActivity } from '../utils/auditLog';

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(2)}%`;

const TIPO_FONDOS = ['CETES', 'GBM+', 'Fondo de Inversion', 'Bursatec', 'Acciones', 'Dolares', 'Otro'];

// A = P(1 + r/12)^n
const proyectar = (monto, tasaAnual, meses) => {
  if (!monto || !tasaAnual || !meses) return monto || 0;
  return monto * Math.pow(1 + tasaAnual / 100 / 12, meses);
};

const SEED = [
  { person: 'Tumi',    tipo: 'CETES',             fechaInversion: '2024-01-01', monto: 50000,  tasaInteres: 11.5, mesesInversion: 12 },
  { person: 'Alberto', tipo: 'GBM+',              fechaInversion: '2024-03-01', monto: 100000, tasaInteres: 13.0, mesesInversion: 24 },
  { person: 'Tumi',    tipo: 'Fondo de Inversion', fechaInversion: '2024-06-01', monto: 75000,  tasaInteres: 9.8,  mesesInversion: 36 },
];

async function seedFondos(db) {
  const snap = await getDocs(collection(db, 'fondos'));
  if (snap.empty) {
    for (const item of SEED) await addDoc(collection(db, 'fondos'), item);
    const newSnap = await getDocs(collection(db, 'fondos'));
    return newSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

const personColor = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

const BLANK_MODAL = {
  id: null, person: 'Tumi', tipo: 'CETES', fechaInversion: '', monto: 0, tasaInteres: 0, mesesInversion: 12, saving: false,
};

export default function Fondos({ db, user }) {
  const { t } = useSettings();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  useEffect(() => {
    (async () => {
      try { setRows(await seedFondos(db)); }
      catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  const totalMonto      = rows.reduce((s, r) => s + (r.monto || 0), 0);
  const totalProyectado = rows.reduce((s, r) => s + proyectar(r.monto, r.tasaInteres, r.mesesInversion), 0);
  const totalGanancia   = totalProyectado - totalMonto;

  const openAdd  = () => setModal({ ...BLANK_MODAL });
  const openEdit = (row) => setModal({
    id: row.id, person: row.person, tipo: row.tipo || 'CETES',
    fechaInversion: row.fechaInversion || '', monto: row.monto || 0,
    tasaInteres: row.tasaInteres || 0, mesesInversion: row.mesesInversion || 12,
    saving: false,
  });

  const handleSave = async () => {
    if (!modal) return;
    setModal(m => ({ ...m, saving: true }));
    try {
      const data = {
        person:         modal.person,
        tipo:           modal.tipo,
        fechaInversion: modal.fechaInversion || '',
        monto:          parseFloat(modal.monto) || 0,
        tasaInteres:    parseFloat(modal.tasaInteres) || 0,
        mesesInversion: parseFloat(modal.mesesInversion) || 0,
      };
      if (modal.id) {
        await updateDoc(doc(db, 'fondos', modal.id), data);
        setRows(prev => prev.map(r => r.id === modal.id ? { ...r, ...data } : r));
        await logActivity(db, user, 'editar', 'Fondo', { persona: modal.person, tipo: modal.tipo, monto: modal.monto, tasa: modal.tasaInteres, meses: modal.mesesInversion });
        showToast('Fondo actualizado');
      } else {
        const ref = await addDoc(collection(db, 'fondos'), data);
        setRows(prev => [...prev, { id: ref.id, ...data }]);
        await logActivity(db, user, 'crear', 'Fondo', { persona: modal.person, tipo: modal.tipo, monto: modal.monto, tasa: modal.tasaInteres, meses: modal.mesesInversion });
        showToast('Fondo agregado');
      }
      setModal(null);
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setModal(m => ({ ...m, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!modal?.id) return;
    try {
      await deleteDoc(doc(db, 'fondos', modal.id));
      setRows(prev => prev.filter(r => r.id !== modal.id));
      await logActivity(db, user, 'eliminar', 'Fondo', { persona: modal.person, tipo: modal.tipo, monto: modal.monto });
      setModal(null);
      showToast('Fondo eliminado');
    } catch (err) {
      console.error(err);
      showToast('Error al eliminar', 'error');
    }
  };

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  const modalProy  = proyectar(parseFloat(modal?.monto) || 0, parseFloat(modal?.tasaInteres) || 0, parseFloat(modal?.mesesInversion) || 0);
  const modalGain  = modalProy - (parseFloat(modal?.monto) || 0);

  return (
    <div className="page-container">
      {/* Title + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t('funds')} <span style={mxnBadge}>MXN</span></h1>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#1a472a', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 0.875rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          <Plus size={16} /> {t('add')}
        </button>
      </div>

      {/* Summary cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-card-label">Total {t('invested')}</div>
          <div className="summary-card-value">{fmt(totalMonto)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Total {t('projected')}</div>
          <div className="summary-card-value" style={{ color: '#10b981' }}>{fmt(totalProyectado)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('gain')} Estimada</div>
          <div className="summary-card-value" style={{ color: '#3b82f6' }}>+{fmt(totalGanancia)}</div>
        </div>
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Sin fondos. Agrega uno con el boton +.
          </div>
        )}

        {rows.map(row => {
          const proyectado = proyectar(row.monto, row.tasaInteres, row.mesesInversion);
          const ganancia   = proyectado - (row.monto || 0);
          return (
            <div key={row.id} onClick={() => openEdit(row)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: '1px solid var(--border-color)', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}>
              {/* Person color bar */}
              <div style={{ width: 4, height: 56, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />

              {/* Center info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person) }}>{row.person}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', borderRadius: '0.25rem', padding: '0.05rem 0.4rem' }}>{row.tipo}</span>
                  <span style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 600 }}>{fmtPct(row.tasaInteres)}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>{row.mesesInversion}m</span>
                </div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(row.monto)}</div>
                {row.fechaInversion && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-light)', marginTop: '0.1rem' }}>{t('investDate')}: {row.fechaInversion}</div>
                )}
              </div>

              {/* Right: proyectado + ganancia */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#10b981' }}>{fmt(proyectado)}</div>
                <div style={{ fontSize: '0.72rem', color: '#3b82f6', fontWeight: 600, marginTop: '0.1rem' }}>+{fmt(ganancia)}</div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              {modal.id ? t('edit') : t('add')} Fondo
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>{t('person')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${modal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: modal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo */}
              <div>
                <label style={labelStyle}>{t('tipo')}</label>
                <select value={modal.tipo} onChange={e => setModal(m => ({ ...m, tipo: e.target.value }))} style={inputStyle}>
                  {TIPO_FONDOS.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </div>

              {/* Fecha Inversion */}
              <div>
                <label style={labelStyle}>{t('investDate')}</label>
                <input type="date" value={modal.fechaInversion} onChange={e => setModal(m => ({ ...m, fechaInversion: e.target.value }))} style={inputStyle} />
              </div>

              {/* Monto */}
              <div>
                <label style={labelStyle}>{t('invested')}</label>
                <input type="number" inputMode="decimal" value={modal.monto} onChange={e => setModal(m => ({ ...m, monto: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>

              {/* Tasa Interes */}
              <div>
                <label style={labelStyle}>{t('rate')} Anual (%)</label>
                <input type="number" inputMode="decimal" step="0.01" value={modal.tasaInteres} onChange={e => setModal(m => ({ ...m, tasaInteres: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>

              {/* Meses */}
              <div>
                <label style={labelStyle}>{t('months')}</label>
                <input type="number" inputMode="numeric" value={modal.mesesInversion} onChange={e => setModal(m => ({ ...m, mesesInversion: e.target.value }))} placeholder="12" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>

              {/* Proyectado + Ganancia (read-only) */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{t('projected')}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#10b981' }}>{fmt(modalProy)}</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{t('gain')}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#3b82f6' }}>+{fmt(modalGain)}</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {modal.id && (
                <button onClick={handleDelete}
                  style={{ padding: '0.7rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Trash2 size={15} />{t('delete')}
                </button>
              )}
              <button onClick={() => setModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                {t('cancel')}
              </button>
              <button onClick={handleSave} disabled={modal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: modal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: modal.saving ? 0.7 : 1 }}>
                {modal.saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
